import { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/Layout';
import axios from 'axios';
import { FileText, Wand2, Search, ExternalLink, Image, X, Loader2, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// NBA Players list (2025-26 season top players)
const NBA_PLAYERS = [
  "LeBron James", "Stephen Curry", "Kevin Durant", "Giannis Antetokounmpo", "Luka Doncic",
  "Jayson Tatum", "Joel Embiid", "Nikola Jokic", "Ja Morant", "Damian Lillard",
  "Anthony Edwards", "Devin Booker", "Trae Young", "Donovan Mitchell", "Kyrie Irving",
  "Zion Williamson", "Anthony Davis", "Paul George", "Kawhi Leonard", "Jimmy Butler",
  "Bam Adebayo", "Tyrese Haliburton", "De'Aaron Fox", "Shai Gilgeous-Alexander", "Chet Holmgren",
  "Victor Wembanyama", "Paolo Banchero", "Scottie Barnes", "Evan Mobley", "Cade Cunningham",
  "Jalen Green", "Tyrese Maxey", "Desmond Bane", "Jaren Jackson Jr", "Darius Garland",
  "Fred VanVleet", "Jalen Brunson", "Julius Randle", "RJ Barrett", "OG Anunoby",
  "Pascal Siakam", "Myles Turner", "Buddy Hield", "CJ McCollum", "Brandon Ingram",
  "Zach LaVine", "DeMar DeRozan", "Khris Middleton", "Brook Lopez", "Bobby Portis",
  "Lauri Markkanen", "Jordan Clarkson", "Collin Sexton", "Anfernee Simons", "Jerami Grant",
  "Domantas Sabonis", "Keegan Murray", "Franz Wagner", "Jalen Williams", "Josh Giddey",
  "Alperen Sengun", "Jabari Smith Jr", "Amen Thompson", "Ausar Thompson", "Cam Thomas",
  "Mikal Bridges", "Cameron Johnson", "Spencer Dinwiddie", "Derrick White", "Jaylen Brown",
  "Al Horford", "Marcus Smart", "Robert Williams III", "Karl-Anthony Towns", "Rudy Gobert",
  "Mike Conley", "Naz Reid", "Jaden McDaniels", "Austin Reaves", "D'Angelo Russell",
  "Rui Hachimura", "Jarred Vanderbilt", "Christian Wood", "Mo Bamba", "Wendell Carter Jr",
  "Markelle Fultz", "Jonathan Isaac", "Cole Anthony", "Gary Trent Jr", "Immanuel Quickley",
  "Jakob Poeltl", "Chris Boucher", "Precious Achiuwa", "Terry Rozier", "LaMelo Ball",
  "Miles Bridges", "Gordon Hayward", "Nick Richards", "Mark Williams", "PJ Washington",
  "Kyle Kuzma", "Bradley Beal", "Jordan Poole", "Corey Kispert", "Deni Avdija",
  "Trey Murphy III", "Herbert Jones", "Jonas Valanciunas", "Dyson Daniels", "Larry Nance Jr",
  "John Collins", "Dejounte Murray", "Bogdan Bogdanovic", "Onyeka Okongwu", "Clint Capela",
  "De'Andre Hunter", "Jalen Johnson", "AJ Griffin", "Keon Johnson", "Saddiq Bey"
].sort();

export default function ThumbnailCreator() {
  const [style, setStyle] = useState('3');
  const [headline, setHeadline] = useState('FREE PICKS');
  const [subtext, setSubtext] = useState('NBA THURSDAY');
  const [player, setPlayer] = useState('');
  const [stat, setStat] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [record, setRecord] = useState({ wins: 0, losses: 0, pending: 0 });
  const [showReportParser, setShowReportParser] = useState(false);
  const [reportText, setReportText] = useState('');
  const [parseStatus, setParseStatus] = useState('');
  
  // Player search state
  const [playerSearch, setPlayerSearch] = useState('');
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [searchingImages, setSearchingImages] = useState(false);
  const [imageResults, setImageResults] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Handle local image upload — converts to base64 data URL to preserve full quality
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    setUploadingImage(true);
    try {
      // Upload to backend temp storage to get a URL that html2canvas can use
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/temp-image/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Use the tempid approach for the thumbnail generator
      setImageUrl(`tempid:${response.data.image_id}`);
    } catch (error) {
      // Fallback: read as base64 data URL (works for preview, may have CORS issues with html2canvas)
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageUrl(event.target.result);
      };
      reader.readAsDataURL(file);
    }
    setUploadingImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const response = await axios.get(`${API}/results/summary`);
        const { wins, losses, pushes } = response.data;
        setRecord({ wins, losses, pending: pushes });
      } catch (error) {
        console.error('Failed to fetch record:', error);
      }
    };
    fetchRecord();
  }, []);

  const winRate = record.wins + record.losses > 0 
    ? ((record.wins / (record.wins + record.losses)) * 100).toFixed(1)
    : '0';

  const days = ['NBA MONDAY', 'NBA TUESDAY', 'NBA WEDNESDAY', 'NBA THURSDAY', 'NBA FRIDAY', 'NBA SATURDAY', 'NBA SUNDAY'];

  // Filter players based on search
  const filteredPlayers = NBA_PLAYERS.filter(p => 
    p.toLowerCase().includes(playerSearch.toLowerCase())
  ).slice(0, 10);

  // Select player from dropdown
  const selectPlayer = (selectedPlayer) => {
    setPlayer(selectedPlayer.toUpperCase());
    setPlayerSearch(selectedPlayer);
    setShowPlayerDropdown(false);
  };

  // Search for player images
  const searchPlayerImages = async () => {
    if (!player) return;
    
    setSearchingImages(true);
    setShowImageSearch(true);
    
    // Generate search URLs for different sources
    const playerName = player.toLowerCase().replace(/\s+/g, '+');
    const playerSlug = player.toLowerCase().replace(/\s+/g, '-');
    
    // Create image source links
    const imageSources = [
      {
        name: 'Google Images',
        url: `https://www.google.com/search?tbm=isch&q=${playerName}+nba+png+transparent`,
        description: 'Best for finding PNG cutouts'
      },
      {
        name: 'NBA.com',
        url: `https://www.nba.com/search?filters=player&q=${playerName}`,
        description: 'Official player headshots'
      },
      {
        name: 'ESPN',
        url: `https://www.espn.com/search/_/q/${playerName}`,
        description: 'High quality action shots'
      },
      {
        name: 'Pinterest',
        url: `https://www.pinterest.com/search/pins/?q=${playerName}+nba+png`,
        description: 'Community-made cutouts'
      }
    ];
    
    setImageResults(imageSources);
    setSearchingImages(false);
  };

  // Parse the Intelligence Report
  const parseReport = () => {
    try {
      const text = reportText;
      
      // Extract player name
      const propMatch = text.match(/PROP\s*\d*\s*[–-]\s*([A-Za-z\s]+?)\s*[–-]\s*(Under|Over)/i);
      let extractedPlayer = '';
      
      if (propMatch) {
        extractedPlayer = propMatch[1].trim().toUpperCase();
      }
      
      // Extract the line/stat
      const lineMatch = text.match(/(Under|Over)\s*(\d+\.?\d*)\s*([A-Za-z\s\+]+?)(?:\n|Market)/i);
      let extractedStat = '';
      
      if (lineMatch) {
        const direction = lineMatch[1];
        const number = lineMatch[2];
        let statType = lineMatch[3].trim();
        
        statType = statType
          .replace(/Points\s*\+\s*Assists/i, 'PTS+AST')
          .replace(/Points/i, 'PTS')
          .replace(/Assists/i, 'AST')
          .replace(/Rebounds/i, 'REB')
          .replace(/Threes/i, '3PM')
          .replace(/Three Pointers Made/i, '3PM')
          .replace(/Steals/i, 'STL')
          .replace(/Blocks/i, 'BLK');
        
        extractedStat = `${direction} ${number} ${statType}`;
      }
      
      // Extract date to determine day
      const dateMatch = text.match(/Date:\s*(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        const date = new Date(dateMatch[1]);
        const dayName = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][date.getDay()];
        setSubtext(`NBA ${dayName}`);
      }
      
      // Set extracted values
      if (extractedPlayer) {
        setPlayer(extractedPlayer);
        setPlayerSearch(extractedPlayer);
      }
      if (extractedStat) setStat(extractedStat);
      
      // Build status message
      let status = [];
      if (extractedPlayer) status.push(`Player: ${extractedPlayer}`);
      if (extractedStat) status.push(`Stat: ${extractedStat}`);
      
      if (status.length > 0) {
        setParseStatus(`✅ Extracted: ${status.join(' | ')}`);
      } else {
        setParseStatus('⚠️ Could not extract data. Make sure you pasted the full report.');
      }
      
      setShowReportParser(false);
    } catch (error) {
      setParseStatus('❌ Error parsing report. Try again.');
    }
  };

  const previewUrl = `${window.location.origin}/thumbnail?style=${style}&headline=${encodeURIComponent(headline)}&subtext=${encodeURIComponent(subtext)}&player=${encodeURIComponent(player)}&stat=${encodeURIComponent(stat)}&image=${encodeURIComponent(imageUrl)}`;

  return (
    <Layout>
      <div className="container mx-auto px-6 py-8">
        <h1 className="text-3xl font-heading font-bold mb-2">Thumbnail Creator</h1>
        <p className="text-muted-foreground mb-8">Create YouTube thumbnails with your branding. Paste your Intelligence Report to auto-fill!</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Controls Panel */}
          <div className="space-y-6">
            {/* Report Parser Button */}
            <div className="bg-primary/10 border border-primary/30 p-4">
              <button
                onClick={() => setShowReportParser(!showReportParser)}
                className="w-full flex items-center justify-center gap-3 bg-primary text-black font-bold py-3 px-6 hover:bg-primary/90 transition-colors"
              >
                <FileText size={20} />
                <span>Paste Intelligence Report (Auto-Fill)</span>
                <Wand2 size={20} />
              </button>
              {parseStatus && (
                <p className="text-sm mt-3 text-center">{parseStatus}</p>
              )}
            </div>

            {/* Report Parser Modal */}
            {showReportParser && (
              <div className="bg-card border border-border p-6">
                <h3 className="text-lg font-heading font-bold mb-3">Paste Your Intelligence Report</h3>
                <textarea
                  value={reportText}
                  onChange={(e) => setReportText(e.target.value)}
                  placeholder="Paste your SLATE INTELLIGENCE MEMO here..."
                  className="w-full bg-background border border-border p-3 text-foreground text-sm h-48 font-mono"
                />
                <div className="flex gap-3 mt-3">
                  <button onClick={parseReport} className="flex-1 bg-primary text-black font-bold py-2 px-4 hover:bg-primary/90">
                    Extract Data
                  </button>
                  <button onClick={() => setShowReportParser(false)} className="px-4 py-2 border border-border hover:border-primary">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="bg-card border border-border p-6 space-y-4">
              <h2 className="text-xl font-heading font-bold mb-4">Customize</h2>
              
              {/* Style Selector */}
              <div>
                <label className="block text-sm font-medium mb-2">Style</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: '1', name: 'Style 1', desc: 'Side Panel' },
                    { id: '2', name: 'Style 2', desc: 'Centered' },
                    { id: '3', name: 'Style 3', desc: 'Player Focus' },
                    { id: '6', name: 'Twitter', desc: 'Twitter Optimized' }
                  ].map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setStyle(s.id)}
                      className={`p-3 border-2 transition-all ${style === s.id ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}`}
                    >
                      <div className="text-lg font-bold">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Day Selector */}
              <div>
                <label className="block text-sm font-medium mb-2">Day Badge</label>
                <select 
                  value={subtext}
                  onChange={(e) => setSubtext(e.target.value)}
                  className="w-full bg-background border border-border p-3 text-foreground"
                >
                  {days.map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>

              {/* Headline */}
              <div>
                <label className="block text-sm font-medium mb-2">Headline</label>
                <input
                  type="text"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="FREE PICKS"
                  className="w-full bg-background border border-border p-3 text-foreground"
                />
              </div>

              {/* Player Name with Search */}
              <div className="relative">
                <label className="block text-sm font-medium mb-2">Player Name</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={playerSearch}
                      onChange={(e) => {
                        setPlayerSearch(e.target.value);
                        setPlayer(e.target.value.toUpperCase());
                        setShowPlayerDropdown(true);
                      }}
                      onFocus={() => setShowPlayerDropdown(true)}
                      placeholder="Search NBA players..."
                      className="w-full bg-background border border-border p-3 text-foreground"
                    />
                    {/* Player Dropdown */}
                    {showPlayerDropdown && playerSearch && filteredPlayers.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-card border border-border max-h-48 overflow-y-auto">
                        {filteredPlayers.map((p) => (
                          <button
                            key={p}
                            onClick={() => selectPlayer(p)}
                            className="w-full text-left px-4 py-2 hover:bg-primary/10 transition-colors"
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={searchPlayerImages}
                    disabled={!player}
                    className="bg-primary text-black px-4 py-2 font-bold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Image size={18} />
                    <span className="hidden sm:inline">Find Images</span>
                  </button>
                </div>
              </div>

              {/* Stat/Line */}
              <div>
                <label className="block text-sm font-medium mb-2">Stat/Line</label>
                <input
                  type="text"
                  value={stat}
                  onChange={(e) => setStat(e.target.value)}
                  placeholder="Under 23.5 PTS+AST"
                  className="w-full bg-background border border-border p-3 text-foreground"
                />
              </div>

              {/* Image URL or Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">Player Image</label>
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="flex items-center gap-2 bg-primary text-black px-4 py-2 font-bold hover:bg-primary/90 disabled:opacity-50"
                  >
                    {uploadingImage ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    {uploadingImage ? 'Uploading...' : 'Upload Image'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <span className="self-center text-muted-foreground text-sm">or paste URL below</span>
                </div>
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="Paste image URL here..."
                  className="w-full bg-background border border-border p-3 text-foreground text-sm"
                />
                {imageUrl && imageUrl.startsWith('tempid:') && (
                  <p className="text-xs text-primary mt-1">Uploaded image ready (full quality preserved)</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Tip: Upload directly for best quality. Use <a href="https://remove.bg" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">remove.bg</a> to remove backgrounds first.
                </p>
              </div>
            </div>

            {/* Image Search Results Modal */}
            {showImageSearch && (
              <div className="bg-card border border-primary p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-heading font-bold">Find {player} Images</h3>
                  <button onClick={() => setShowImageSearch(false)} className="text-muted-foreground hover:text-foreground">
                    <X size={20} />
                  </button>
                </div>
                
                {searchingImages ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin" size={32} />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground mb-4">
                      Click a source below to search for images. Find one you like, right-click → "Copy Image Address", then paste it above.
                    </p>
                    {imageResults.map((source, i) => (
                      <a
                        key={i}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-4 border border-border hover:border-primary hover:bg-primary/5 transition-all"
                      >
                        <div>
                          <div className="font-bold text-primary">{source.name}</div>
                          <div className="text-sm text-muted-foreground">{source.description}</div>
                        </div>
                        <ExternalLink size={18} className="text-muted-foreground" />
                      </a>
                    ))}
                    
                    <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30">
                      <p className="text-sm text-yellow-500">
                        <strong>Pro tip:</strong> Search for "{player} PNG transparent" for best results. Then use remove.bg if needed.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Current Stats */}
            <div className="bg-card border border-border p-6">
              <h3 className="text-lg font-heading font-bold mb-3">Your Live Stats</h3>
              <div className="flex gap-6">
                <div>
                  <div className="text-2xl font-bold text-primary">{winRate}%</div>
                  <div className="text-xs text-muted-foreground">Win Rate</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{record.wins}-{record.losses}</div>
                  <div className="text-xs text-muted-foreground">Record</div>
                </div>
              </div>
            </div>

            {/* Get Thumbnail Button */}
            <div className="bg-card border border-border p-6">
              <a 
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-primary text-black font-bold py-3 px-6 hover:bg-primary/90 transition-colors"
              >
                <ExternalLink size={18} />
                Open Full-Size Thumbnail
              </a>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Right-click → Save Image, or take a screenshot
              </p>
            </div>
          </div>

          {/* Preview Panel */}
          <div>
            <h2 className="text-xl font-heading font-bold mb-4">Preview</h2>
            <div className="bg-black border border-border overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <div className="w-full h-full relative overflow-hidden">
                {style === '3' ? (
                  <div className="w-full h-full bg-gradient-to-r from-black via-gray-900 to-black relative flex">
                    {imageUrl && (
                      <div className="absolute left-0 bottom-0 h-full flex items-end z-10">
                        <img src={imageUrl} alt="Player" className="h-full w-auto object-contain object-bottom" style={{ filter: 'drop-shadow(0 0 30px rgba(74,222,128,0.4))' }} />
                      </div>
                    )}
                    <div className="absolute right-0 top-0 h-full w-[55%] flex flex-col justify-center pr-6 z-20">
                      <div className="mb-2">
                        <span className="bg-primary text-black px-3 py-1 text-xs font-bold uppercase tracking-widest">{subtext}</span>
                      </div>
                      {player && <h1 className="text-3xl md:text-4xl font-heading font-black text-white leading-tight mb-1">{player}</h1>}
                      {stat && <div className="text-xl md:text-2xl font-heading text-primary mb-3">{stat}</div>}
                      <h2 className="text-2xl md:text-3xl font-heading font-black mb-4">
                        <span className="text-primary">{headline.split(' ')[0]}</span>
                        <span className="text-white"> {headline.split(' ').slice(1).join(' ')}</span>
                      </h2>
                      <div className="flex gap-4">
                        <div className="bg-black/50 border border-primary/30 px-4 py-2">
                          <div className="text-xl font-black text-primary">{winRate}%</div>
                          <div className="text-gray-500 font-mono text-[10px] uppercase">Win Rate</div>
                        </div>
                        <div className="bg-black/50 border border-primary/30 px-4 py-2">
                          <div className="text-xl font-black text-white">{record.wins}-{record.losses}</div>
                          <div className="text-gray-500 font-mono text-[10px] uppercase">Record</div>
                        </div>
                      </div>
                      <div className="mt-4 flex items-center gap-2">
                        <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
                          <span className="text-black font-bold text-xs">PSL</span>
                        </div>
                        <span className="text-gray-400 font-mono text-xs">PRIMESTATLAB.COM</span>
                      </div>
                    </div>
                    <div className="absolute inset-0 opacity-30 pointer-events-none">
                      <div className="absolute top-0 right-1/4 w-64 h-64 bg-primary rounded-full blur-[100px]"></div>
                    </div>
                  </div>
                ) : style === '2' ? (
                  <div className="w-full h-full bg-black relative flex items-center justify-center">
                    {imageUrl && (
                      <div className="absolute left-0 bottom-0 h-full flex items-end z-0 opacity-40">
                        <img src={imageUrl} alt="Player" className="h-full w-auto object-contain" />
                      </div>
                    )}
                    <div className="text-center relative z-10">
                      <div className="mb-3"><span className="bg-primary text-black px-4 py-1 text-sm font-bold uppercase">{subtext}</span></div>
                      <h1 className="text-5xl font-heading font-black leading-none mb-2">
                        <span className="text-primary">{headline.split(' ')[0]}</span><br />
                        <span className="text-white">{headline.split(' ').slice(1).join(' ')}</span>
                      </h1>
                      {player && <div className="text-2xl font-heading text-gray-300 mb-2">{player} <span className="text-primary">{stat}</span></div>}
                      <div className="flex justify-center gap-6 mt-4">
                        <div><div className="text-2xl font-black text-primary">{winRate}%</div><div className="text-gray-500 font-mono text-xs">Win Rate</div></div>
                        <div><div className="text-2xl font-black text-white">{record.wins}-{record.losses}</div><div className="text-gray-500 font-mono text-xs">Record</div></div>
                      </div>
                    </div>
                  </div>
                ) : style === '6' ? (
                  /* Twitter Optimized - 2:1 aspect ratio friendly */
                  <div className="w-full h-full bg-black relative">
                    {/* Top Bar - PRIME STAT LAB - Always visible at top */}
                    <div className="absolute top-0 left-0 right-0 z-30 bg-gradient-to-r from-primary via-primary to-green-400 py-2 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
                            <span className="text-primary font-black text-sm">PSL</span>
                          </div>
                          <span className="text-black font-heading font-black text-xl tracking-tight">PRIME STAT LAB</span>
                        </div>
                        <span className="text-black font-mono text-sm font-bold">PRIMESTATLAB.COM</span>
                      </div>
                    </div>
                    
                    {/* Main Content Area */}
                    <div className="absolute inset-0 pt-12 flex">
                      {/* Left Side - Player Image */}
                      {imageUrl && (
                        <div className="w-[40%] h-full relative">
                          <img 
                            src={imageUrl} 
                            alt="Player" 
                            className="absolute bottom-0 left-0 h-[95%] w-auto object-contain"
                            style={{ filter: 'drop-shadow(0 0 20px rgba(74,222,128,0.5))' }}
                          />
                        </div>
                      )}
                      
                      {/* Right Side - Info */}
                      <div className={`flex-1 flex flex-col justify-center ${imageUrl ? '' : 'pl-8'} pr-6`}>
                        {/* Day Badge */}
                        <div className="mb-2">
                          <span className="bg-primary/20 text-primary px-3 py-1 text-sm font-mono uppercase border border-primary/30">
                            {subtext}
                          </span>
                        </div>
                        
                        {/* Headline */}
                        <h1 className="text-3xl font-heading font-black mb-2">
                          <span className="text-primary">{headline.split(' ')[0]}</span>
                          <span className="text-white"> {headline.split(' ').slice(1).join(' ')}</span>
                        </h1>
                        
                        {/* Player & Prop */}
                        {player && (
                          <div className="text-xl font-heading text-white mb-3">
                            {player} <span className="text-primary">{stat}</span>
                          </div>
                        )}
                        
                        {/* Stats Row */}
                        <div className="flex gap-4 mb-3">
                          <div className="bg-black/60 border border-primary/50 px-4 py-2">
                            <div className="text-2xl font-black text-primary">{winRate}%</div>
                            <div className="text-gray-400 font-mono text-[10px] uppercase">Win Rate</div>
                          </div>
                          <div className="bg-black/60 border border-white/20 px-4 py-2">
                            <div className="text-2xl font-black text-white">{record.wins}-{record.losses}</div>
                            <div className="text-gray-400 font-mono text-[10px] uppercase">Record</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* PrizePicks Logo Area - Right Side */}
                      <div className="w-[80px] flex flex-col items-center justify-center pr-4">
                        <div className="bg-[#7B2D8E] rounded-xl p-3 mb-2">
                          <span className="text-white font-black text-xl">PP</span>
                        </div>
                        <span className="text-white/60 font-mono text-[8px] uppercase text-center">PRIZE<br/>PICKS</span>
                      </div>
                    </div>
                    
                    {/* Glow Effects */}
                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                      <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-primary/20 rounded-full blur-[80px]"></div>
                      <div className="absolute bottom-0 right-1/4 w-32 h-32 bg-purple-500/20 rounded-full blur-[60px]"></div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-black via-gray-900 to-black relative flex">
                    {imageUrl && (
                      <div className="absolute left-0 bottom-0 h-full flex items-end z-20">
                        <img src={imageUrl} alt="Player" className="h-[95%] w-auto object-contain" style={{ filter: 'drop-shadow(0 0 30px rgba(74,222,128,0.3))' }} />
                      </div>
                    )}
                    <div className={`flex-1 flex flex-col justify-center ${imageUrl ? 'pl-[45%]' : 'pl-8'} pr-4 relative z-10`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-primary rounded flex items-center justify-center"><span className="text-white font-bold text-sm">PSL</span></div>
                        <span className="text-white font-heading text-lg">PRIMESTATLAB</span>
                      </div>
                      <div className="mb-2"><span className="bg-primary/20 text-primary px-3 py-1 text-sm font-mono uppercase">{subtext}</span></div>
                      <h1 className="text-4xl font-heading font-black text-white leading-none mb-2">
                        {headline.split(' ').map((word, i) => (<span key={i} className={i === 0 ? 'text-primary' : 'text-white'}>{word} </span>))}
                      </h1>
                      {player && <div className="text-xl font-heading text-white/80 mb-2">{player} <span className="text-primary">{stat}</span></div>}
                    </div>
                    <div className="w-[200px] flex items-center justify-center pr-4">
                      <div className="bg-black/60 border border-primary/50 p-4">
                        <div className="text-center mb-2"><div className="text-3xl font-heading font-black text-primary">{winRate}%</div><div className="text-gray-400 font-mono text-[10px] uppercase">Win Rate</div></div>
                        <div className="flex justify-center gap-3 text-center">
                          <div className="text-lg font-bold text-green-500">{record.wins}W</div>
                          <div className="text-lg font-bold text-red-500">{record.losses}L</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30">
              <p className="text-sm text-yellow-500">
                <strong>How to save:</strong> Click "Open Full-Size Thumbnail" then screenshot or right-click → Save Image
              </p>
            </div>

            <div className="mt-4 bg-card border border-border p-4">
              <h3 className="font-heading font-bold mb-2">Quick Workflow</h3>
              <ol className="text-sm text-muted-foreground space-y-1">
                <li>1. Paste Intelligence Report OR search for player</li>
                <li>2. Upload a high-res image OR click "Find Images" for sources</li>
                <li>3. Choose your style and customize text</li>
                <li>4. Click "Open Full-Size Thumbnail"</li>
                <li>5. Download as PNG (best quality) or JPG (YouTube optimized)</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
